import { Link } from 'react-router';
import type { ControllerSiteReach } from '@/hooks/useDeviceActions';
import { deviceCount } from '@/utils/formatting';
import { cn } from '@/lib/utils';

/**
 * "Whole site: Default on UOS Server — 7 devices": what a site-scoped
 * operation changes. An operation on a controller site setting reaches every
 * device on the site, not just the one it was run from, and the operator is
 * told so before anything runs.
 */
export function SiteReachLine({ site, className }: { site: ControllerSiteReach; className?: string }) {
  return (
    <p className={cn('text-[11px] text-muted-foreground', className)}>
      <span className="font-semibold text-foreground">Whole site:</span>{' '}
      {site.name ?? site.id} on {site.controller} — {deviceCount(site.devices.length)}
    </p>
  );
}

/** The reach line with every device on the site, for the run sheet and the CM step. */
export function SiteReachDetail({ site, className }: { site: ControllerSiteReach; className?: string }) {
  return (
    <div className={cn('rounded-md border border-sev-3 bg-sev-3-soft p-3', className)}>
      <SiteReachLine site={site} className="text-sm" />
      <p className="text-xs text-muted-foreground mt-1">
        {site.controller} keeps this setting for the whole site and applies it to every device on it:
      </p>
      <ul className="mt-2 text-xs space-y-0.5">
        {site.devices.map((d) => (
          <li key={d.id}>
            <Link to={`/devices/${d.id}`} className="hover:underline">
              {d.name}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
